import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { State } from "../../../domain/model/ResponseState";
import { GetAllProductsUseCase } from "../../../domain/usecase/product/getAll/GetAllUseCase";
import { Product } from "../../../domain/model/Product";
import { GetPerProductReportUseCase } from "../../../domain/usecase/report/getPerProduct/UseCase";
import { downloadBlob, showGlobalLoadingView } from "../../../core/utils/Utils";
import { GetDeliveryPriceUseCase } from "../../../domain/usecase/product/getDeliveryPrice/UseCase";
import { EditDeliveryPriceUseCase } from "../../../domain/usecase/product/editDeliveryPrice/UseCase";
import { useDebounce } from "use-debounce";

export function ProductsViewModel(
  getAllProductsUseCase: GetAllProductsUseCase,
  getPerProductReportUseCase: GetPerProductReportUseCase,
  getDeliveryPriceUseCase: GetDeliveryPriceUseCase,
  editDeliveryPriceUseCase: EditDeliveryPriceUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);

  const [products, setProducts] = useState<Array<Product> | undefined>();

  const [deliveryPrice, setDeliveryPrice] = useState<number>();

  const [debouncedDeliveryPrice] = useDebounce(deliveryPrice, 1500);

  // Ref to track the initial load state
  const isInitialLoad = useRef(true);
  const handleDeliveryPriceChange = (newPrice: number) => {
    setDeliveryPrice(newPrice);
  };

  useEffect(() => {
    if (deliveryPrice == undefined) return;
    if (!isInitialLoad.current) {
      editDeliveryPrice(); // Call debounced update function
    } else {
      isInitialLoad.current = false; // Mark initial load as done
    }
  }, [debouncedDeliveryPrice]);

  useEffect(() => {
    fetchProducts();
    fetchDeliveryPrice();
  }, []);

  async function fetchDeliveryPrice() {
    const response = await getDeliveryPriceUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        setDeliveryPrice(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function editDeliveryPrice() {
    if (deliveryPrice == undefined) return;
    showGlobalLoadingView(true);
    const response = await editDeliveryPriceUseCase.invoke(deliveryPrice);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchProducts() {
    setLoading(true);
    const response = await getAllProductsUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        setProducts(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  async function getPerProductReport(startDate?: Date, endDate?: Date) {
    const startDateString = startDate ? startDate.toUTCString() : undefined;
    const endDateString = endDate ? endDate.toUTCString() : undefined;

    showGlobalLoadingView(true);
    const response = await getPerProductReportUseCase.invoke({
      dateRange: {
        fromUTC: startDateString,
        toUTC: endDateString,
      },
    });
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        downloadBlob(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function setCustomDate(
    startDate: Date | null | undefined,
    endDate: Date | null | undefined
  ) {
    if (!startDate) return;
    if (!endDate) return;

    if (endDate <= startDate) {
      showSnackbar("End date cannot be less than end date");
      return;
    }

    getPerProductReport(startDate, endDate);
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    products,
    isLoading,
    setLoading,
    getPerProductReport,
    setCustomDate,
    deliveryPrice,
    handleDeliveryPriceChange,
  };
}
